import React from 'react';

/**
 * Renders text with **bold** markdown syntax as <strong> elements.
 * Splits on **...** patterns and wraps matches in <strong>.
 */
export function renderBoldText(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * Wraps or unwraps selected text in a textarea with **bold** markers.
 * Works with both controlled (setState) and uncontrolled (ref) textareas.
 *
 * @param {HTMLTextAreaElement} textarea - The textarea DOM element
 * @param {Function} [onChange] - Optional state setter for controlled textareas
 */
export function toggleBold(textarea, onChange) {
  return toggleWrap(textarea, onChange, '**');
}

/**
 * Same as toggleBold but for *italic* markers.
 */
export function toggleItalic(textarea, onChange) {
  return toggleWrap(textarea, onChange, '*');
}

/**
 * Generic wrap/unwrap helper. Supports any marker (e.g. **, *, __, _).
 */
function toggleWrap(textarea, onChange, marker) {
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = textarea.value;
  
  if (start === end) return; // No selection
  
  const before = text.substring(0, start);
  const selected = text.substring(start, end);
  const after = text.substring(end);
  const m = marker;
  const mlen = m.length;
  
  let newText;
  let newCursorStart, newCursorEnd;
  
  // Check if selection is already wrapped with marker
  if (selected.startsWith(m) && selected.endsWith(m) && selected.length > mlen * 2) {
    // Unwrap: remove marker from both ends
    const inner = selected.slice(mlen, -mlen);
    newText = before + inner + after;
    newCursorStart = start;
    newCursorEnd = start + inner.length;
  } else if (before.endsWith(m) && after.startsWith(m)) {
    // Unwrap: remove marker from surrounding context
    newText = before.slice(0, -mlen) + selected + after.slice(mlen);
    newCursorStart = start - mlen;
    newCursorEnd = newCursorStart + selected.length;
  } else {
    // Wrap in marker
    newText = before + m + selected + m + after;
    newCursorStart = start;
    newCursorEnd = end + mlen * 2;
  }
  
  // Update the textarea
  if (onChange) {
    onChange(newText);
  } else {
    // Uncontrolled — set value directly and dispatch event
    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
    nativeSetter.call(textarea, newText);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }
  
  // Restore selection after React re-renders
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorStart, newCursorEnd);
  });
}

/**
 * Renders inline `**bold**`, `*italic*`, and `[text](url)` markdown across
 * multiple lines/paragraphs. Splits on \n into <p> blocks. Internal links
 * (relative paths or hash anchors) navigate same-tab; external links open
 * in a new tab with `rel="noopener noreferrer"`.
 *
 * Apple-style link presentation: word becomes the link, no arrow trail, no
 * dotted underline, subtle coral underline on hover. Use for accordion-style
 * explanatory copy.
 */
export function renderRichText(text, paragraphClassName = 'mb-3 last:mb-0') {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, lineIdx) => {
    if (!line.trim()) return null;
    const parts = [];
    let key = 0;
    // Order matters: link pattern first (longer), then bold, then italic.
    const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      if (match[2] && match[3]) {
        // [text](url)
        const linkText = match[2];
        const url = match[3];
        const isExternal = /^https?:\/\//i.test(url);
        parts.push(
          <a
            key={`a${key++}`}
            href={url}
            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="text-[#2D2825] hover:text-[#E05A47] border-b border-transparent hover:border-[#E05A47] transition-colors"
          >
            {linkText}
          </a>
        );
      } else if (match[4]) {
        parts.push(<strong key={`b${key++}`}>{match[4]}</strong>);
      } else if (match[5]) {
        parts.push(<em key={`i${key++}`}>{match[5]}</em>);
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    return <p key={lineIdx} className={paragraphClassName}>{parts.length ? parts : line}</p>;
  });
}
